import { useMemo } from 'react';
import { Loader2, CheckCircle2, Dot } from 'lucide-react';
import cn from 'classnames';

type Props = {
  isSaving: boolean;
  isEdited: boolean;
  lastSavedAt: number | null;
  className?: string;
};

export default function SaveStatus({
  isSaving,
  isEdited,
  lastSavedAt,
  className
}: Props) {
  const label = useMemo(() => {
    if (isSaving) return 'Saving…';
    if (isEdited) return 'Edited';
    if (lastSavedAt) {
      const d = new Date(lastSavedAt);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return ` ${hh}:${mm}`;
    }
    return 'Saved';
  }, [isSaving, isEdited, lastSavedAt]);

  const Icon = isSaving ? Loader2 : isEdited ? Dot : CheckCircle2;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ml-2 shadow-lg',
        isSaving
          ? 'border-zinc-300 text-zinc-700 bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:bg-zinc-800'
          : isEdited
            ? 'border-amber-300 text-amber-700 bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:bg-amber-950/40'
            : 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:bg-emerald-950/40',
        className
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <Icon className={cn('h-3.5 w-3.5', isSaving && 'animate-spin')} />
      <span>{label}</span>
    </div>
  );
}
